import { useState } from "react";
import { TextInput, Button } from "../../../../components";
import { GAEventTrack } from "../../../../utils/hooks/GA_events";
import {
  validateEmail,
  validatePassword,
  validatePhone,
} from "../../../../assets/js/validation";

const SchoolRegisterStep1 = ({
  contactPersonName,
  setContactPersonName,
  contactPersonEmail,
  setContactPersonEmail,
  contactPersonPhone,
  setContactPersonPhone,
  schoolName,
  setSchoolName,
  schoolEmail,
  setSchoolEmail,
  password,
  setPassword,
  confirmPassword,
  setConfirmPassword,
  schoolFee,
  setSchoolFee,
  practidoFee,
  onNext,
  error,
  setError,
}) => {
  const [validationErrors, setValidationErrors] = useState({});

  const resetFieldError = (field) => {
    setValidationErrors((prev) => ({ ...prev, [field]: null }));
    setError(null);
  };

  const validateStep1 = () => {
    const errors = {};

    // Contact Person validations
    if (!contactPersonName.trim()) {
      errors.contactPersonName = "Contact Person Name is required.";
    }
    if (!contactPersonPhone.trim()) {
      errors.contactPersonPhone = "Contact Person Phone is required.";
    }
    if (contactPersonEmail && !validateEmail(contactPersonEmail)) {
      errors.contactPersonEmail = "Please enter a valid email id.";
    }
    if (contactPersonPhone && !validatePhone(contactPersonPhone)) {
      errors.contactPersonPhone = "Please enter a valid 10 digit phone number";
    }

    // School basic info validations
    if (!schoolName.trim()) {
      errors.schoolName = "School Name is required.";
    }
    if (!schoolEmail.trim()) {
      errors.schoolEmail = "Email is required.";
    }
    if (schoolEmail && !validateEmail(schoolEmail)) {
      errors.schoolEmail = "Please enter a valid email id.";
    }
    if (!password.trim()) {
      errors.password = "Password is required.";
    }
    if (password && !validatePassword(password)) {
      errors.password =
        "Password should be atleast 8 characters, one uppercase letter, one lowercase letter, and one digit";
    }
    if (!confirmPassword.trim()) {
      errors.confirmPassword = "Confirm Password is required.";
    }
    if (password && confirmPassword && password !== confirmPassword) {
      errors.confirmPassword = "Passwords don't match";
    }

    // Fee validation
    if (schoolFee === null || schoolFee === undefined || schoolFee === "") {
      errors.schoolFee = "School Fee is required.";
    }
    if (schoolFee && (schoolFee < 0 || schoolFee > 99999)) {
      errors.schoolFee = "Please enter a valid fee amount (0-99999)";
    }

    return errors;
  };

  const handleNext = (e) => {
    e.preventDefault();
    const errors = validateStep1();
    // console.log("errors", errors);

    if (Object.keys(errors).length > 0) {
      // Focus first error field

      const firstErrorField = Object.keys(errors)[0];

      const nativeField = e.target?.[firstErrorField];
      if (nativeField && typeof nativeField.focus === "function") {
        nativeField.focus();
      }

      setValidationErrors(errors);
      return;
    }

    // GA: Step 1 next clicked (on valid form)
    GAEventTrack({
      event: "school-reg-step1-next",
      data: { page: "school-register-step1" },
    });

    // Prepare step 1 data
    const step1Data = {
      contactPersonName: contactPersonName.trim(),
      contactPersonEmail: contactPersonEmail.trim(),
      contactPersonPhone: contactPersonPhone.trim(),
      schoolName: schoolName.trim(),
      schoolEmail: schoolEmail.trim(),
      password: password.trim(),
      confirmPassword: confirmPassword.trim(),
      schoolFee: Number(schoolFee),
    };

    onNext(step1Data);
  };

  // DEV ONLY: Fill form with test data for development
  // const fillTestData = () => {
  //   console.log("fillTestData function called");
  //   setContactPersonName("John Smith");
  //   setContactPersonEmail("john.smith@example.com");
  //   setContactPersonPhone("9876543210");
  //   setSchoolName("ABC International School");
  //   setSchoolEmail("admin@abcschool.edu");
  //   setPassword("TestPass123");
  //   setConfirmPassword("TestPass123");
  //   setSchoolFee(500);
  //   setValidationErrors({});
  //   setError(null);
  //   console.log("Test data filled successfully");
  // };

  return (
    <div className="step1-container">
      <form className="wid-100" noValidate onSubmit={handleNext}>
        {/* Contact Person Information */}
        <div className="form-outer-container bg-orange-50 p-4 sm:p-6 rounded-lg mb-6">
          <h2 className="box-heading text-lg sm:text-xl font-bold text-gray-800 mb-4">
            Contact Person Information
          </h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="form-group">
              <label
                htmlFor="contactPersonName"
                className="block text-sm font-medium text-gray-700 mb-2"
              >
                Contact Person Name <span className="text-red-500"> * </span>
              </label>
              <TextInput
                type="text"
                placeholderText="Enter contact person name"
                inputName="contactPersonName"
                isRequired={true}
                idName="contactPersonName"
                inputValue={contactPersonName}
                handleChange={(e) => {
                  setContactPersonName(e.target.value);
                  resetFieldError("contactPersonName");
                }}
                error={validationErrors?.contactPersonName}
              />
            </div>

            <div className="form-group">
              <label
                htmlFor="contactPersonEmail"
                className="block text-sm font-medium text-gray-700 mb-2"
              >
                Contact Person Email
                {/* <span className="text-gray-600 font-normal"> (optional)</span> */}
              </label>
              <TextInput
                type="email"
                placeholderText="Enter contact person email"
                inputName="contactPersonEmail"
                isRequired={false}
                idName="contactPersonEmail"
                inputValue={contactPersonEmail}
                handleChange={(e) => {
                  setContactPersonEmail(e.target.value);
                  resetFieldError("contactPersonEmail");
                }}
                error={validationErrors?.contactPersonEmail}
              />
            </div>

            <div className="form-group sm:col-span-2">
              <label
                htmlFor="contactPersonPhone"
                className="block text-sm font-medium text-gray-700 mb-2"
              >
                Contact Person Phone <span className="text-red-500"> * </span>
              </label>
              <TextInput
                type="tel"
                placeholderText="Enter 10 digit phone number"
                inputName="contactPersonPhone"
                isRequired={true}
                idName="contactPersonPhone"
                inputValue={contactPersonPhone}
                handleChange={(e) => {
                  setContactPersonPhone(e.target.value);
                  resetFieldError("contactPersonPhone");
                }}
                error={validationErrors?.contactPersonPhone}
              />
            </div>
          </div>
        </div>

        {/* School Basic Information */}
        <div className="form-outer-container bg-blue-50 p-4 sm:p-6 rounded-lg mb-6">
          <h2 className="box-heading text-lg sm:text-xl font-bold text-gray-800 mb-4">
            School Basic Information
          </h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="form-group">
              <label
                htmlFor="schoolName"
                className="block text-sm font-medium text-gray-700 mb-2"
              >
                School Name <span className="text-red-500"> * </span>
              </label>
              <TextInput
                type="text"
                placeholderText="Enter school name"
                inputName="schoolName"
                isRequired={true}
                idName="schoolName"
                inputValue={schoolName}
                handleChange={(e) => {
                  setSchoolName(e.target.value);
                  resetFieldError("schoolName");
                }}
                error={validationErrors?.schoolName}
              />
            </div>

            <div className="form-group">
              <label
                htmlFor="schoolEmail"
                className="block text-sm font-medium text-gray-700 mb-2"
              >
                School Email <span className="text-red-500"> * </span>
                <span className="font-normal text-gray-800">
                  (This will be used as login email id)
                </span>
              </label>
              <TextInput
                type="email"
                placeholderText="Enter school email"
                inputName="schoolEmail"
                isRequired={true}
                idName="schoolEmail"
                inputValue={schoolEmail}
                handleChange={(e) => {
                  setSchoolEmail(e.target.value);
                  resetFieldError("schoolEmail");
                }}
                error={validationErrors?.schoolEmail}
              />
            </div>

            <div className="form-group">
              <label
                htmlFor="password"
                className="block text-sm font-medium text-gray-700 mb-2"
              >
                Password <span className="text-red-500"> * </span>
              </label>
              <TextInput
                type="password"
                placeholderText="Enter password"
                inputName="password"
                isRequired={true}
                idName="password"
                inputValue={password}
                handleChange={(e) => {
                  setPassword(e.target.value);
                  resetFieldError("password");
                }}
                error={validationErrors?.password}
              />
            </div>

            <div className="form-group">
              <label
                htmlFor="confirmPassword"
                className="block text-sm font-medium text-gray-700 mb-2"
              >
                Confirm Password <span className="text-red-500"> * </span>
              </label>
              <TextInput
                type="password"
                placeholderText="Confirm password"
                inputName="confirmPassword"
                isRequired={true}
                idName="confirmPassword"
                inputValue={confirmPassword}
                handleChange={(e) => {
                  setConfirmPassword(e.target.value);
                  resetFieldError("confirmPassword");
                }}
                error={validationErrors?.confirmPassword}
              />
            </div>
          </div>
        </div>

        {/* Fee Information */}
        <div className="form-outer-container bg-orange-50 p-4 sm:p-6 rounded-lg mb-6">
          <h2 className="box-heading text-lg sm:text-xl font-bold text-gray-800">
            Fee Information
          </h2>
          <span className="text-sm text-gray-700">
            Parents pay the total fee. Practido's fee is fixed at ₹99 per
            student. Schools can add their own charges in the 'School Fee' box,
            and that amount goes to the school.
          </span>
          <div className="flex flex-wrap gap-2 items-center mt-4">
            <div className="">
              <label
                htmlFor="practidoFee"
                className="block text-sm font-medium text-gray-700 mb-2"
              >
                Practido Fee
              </label>
              <span className="text-4xl font-bold text-gray-800">
                ₹{practidoFee}
              </span>
            </div>
            <span className="text-4xl font-bold text-gray-800 pt-3 mx-4">
              +
            </span>
            <div className="">
              <label
                htmlFor="schoolFee"
                className="block text-sm font-medium text-gray-700 mb-2"
              >
                School Fee
                <span className="text-red-500"> * </span>
              </label>
              <TextInput
                type="number"
                inputName="schoolFee"
                idName="schoolFee"
                placeholderText="00"
                inputValue={schoolFee}
                handleChange={(e) => {
                  setSchoolFee(Number(e.target.value));
                  resetFieldError("schoolFee");
                }}
                isRequired={true}
                width="120px"
                error={validationErrors?.schoolFee}
              />
            </div>
            <span className="text-4xl font-bold text-gray-800 pt-3 mx-4">
              =
            </span>
            <div className="">
              <label
                htmlFor="total"
                className="block text-sm font-medium text-gray-700 mb-2"
              >
                Total
              </label>
              <span className="text-4xl font-bold text-gray-800">
                ₹{practidoFee + (schoolFee || 0)}
              </span>
            </div>
          </div>
        </div>

        {/* Error Display */}
        {error && (
          <div className="mb-4">
            <span className="error-txt text-red-500 text-sm">{error}</span>
          </div>
        )}

        {/* Action Buttons */}
        <div className="flex flex-col sm:flex-row gap-4 justify-end items-center">
          {/* <button
            type="button"
            onClick={() => {
              console.log("Dev Test button clicked");
              fillTestData();
            }}
            className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
          >
            Dev Test
          </button> */}
          <Button
            version="secondary"
            text="Next Step"
            type="submit"
            isDisabled={false}
          />
        </div>
      </form>
    </div>
  );
};

export default SchoolRegisterStep1;
